import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CircularProgress } from '@mui/material';
import AccessGateScreen from '../components/auth/AccessGateScreen';
import {
  fetchAccessGateStatus,
  verifyAccessGatePassword,
  checkAccessGateToken,
  getStoredAccessGateToken,
  setStoredAccessGateToken,
  clearStoredAccessGateToken,
} from '../services/accessGateApi';

interface AccessGateContextType {
  isUnlocked: boolean;
  isGateEnabled: boolean;
  loading: boolean;
  unlock: (password: string) => Promise<boolean>;
}

const AccessGateContext = createContext<AccessGateContextType | undefined>(undefined);

export const useAccessGate = () => {
  const context = useContext(AccessGateContext);
  if (context === undefined) {
    throw new Error('useAccessGate must be used within an AccessGateProvider');
  }
  return context;
};

interface AccessGateProviderProps {
  children: ReactNode;
}

const AccessGateContent: React.FC<AccessGateProviderProps> = ({ children }) => {
  const { isGateEnabled, isUnlocked, loading } = useAccessGate();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#0927eb',
        }}
      >
        <CircularProgress size={60} style={{ color: '#ffffff', marginBottom: '20px' }} />
      </div>
    );
  }

  if (isGateEnabled && !isUnlocked) {
    return <AccessGateScreen />;
  }

  return <>{children}</>;
};

export const AccessGateProvider: React.FC<AccessGateProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isGateEnabled, setIsGateEnabled] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { enabled } = await fetchAccessGateStatus();

        if (!enabled) {
          setIsGateEnabled(false);
          setIsUnlocked(true);
          return;
        }

        setIsGateEnabled(true);

        const storedToken = getStoredAccessGateToken();
        if (storedToken) {
          const { valid } = await checkAccessGateToken(storedToken);
          if (valid) {
            setIsUnlocked(true);
            return;
          }
          clearStoredAccessGateToken();
        }

        setIsUnlocked(false);
      } catch {
        // If the backend is unreachable, don't block the app
        setIsGateEnabled(false);
        setIsUnlocked(true);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, []);

  const unlock = async (password: string): Promise<boolean> => {
    const result = await verifyAccessGatePassword(password);

    if (result.success && result.token) {
      setStoredAccessGateToken(result.token);
      setIsUnlocked(true);
      return true;
    }

    return false;
  };

  const contextValue: AccessGateContextType = {
    isUnlocked,
    isGateEnabled,
    loading,
    unlock,
  };

  return (
    <AccessGateContext.Provider value={contextValue}>
      <AccessGateContent>{children}</AccessGateContent>
    </AccessGateContext.Provider>
  );
};
